(function(){
  if ((window.__cfLoaded = window.__cfLoaded || {})['cf-model3d']) return;
  window.__cfLoaded['cf-model3d'] = true;
const _glbBufferCache = /* @__PURE__ */ new Map();
function loadGlb(url, THREE, GLTFLoader) {
  let bufP = _glbBufferCache.get(url);
  if (!bufP) {
    bufP = fetch(url).then((r) => {
      if (!r.ok) throw new Error("GLB fetch " + r.status + " for " + url);
      return r.arrayBuffer();
    }).catch((e) => {
      _glbBufferCache.delete(url);
      throw e;
    });
    _glbBufferCache.set(url, bufP);
  }
  return bufP.then((buf) => new Promise((resolve, reject) => {
    new GLTFLoader().parse(buf.slice(0), "", resolve, reject);
  }));
}
function Model3DViewer({
  url,
  fallback,
  label,
  autoRotate = true,
  autoRotateSpeed = 0.55,
  height = 540,
  showHint = true,
  className,
  style,
  /* Catalogue GLBs (the hand-tuned ones served from Shopify assets like
   * cf-sheffield-citycentre.glb) already have the slate base + engraved
   * label baked into the mesh, so the viewer's auto-added STL base +
   * holder + tag would double-stack and visually detach the city plate
   * from the base. Set noAccessories=true for those GLBs to skip the
   * external accessories entirely; the picker passes it on whenever a
   * tile resolves to a catalogue handle. */
  noAccessories = false
}) {
  const mountRef = React.useRef(null);
  const [phase, setPhase] = React.useState("loading");
  const [threeReady, setThreeReady] = React.useState(!!window.__cfThree);
  const plateRef = React.useRef(null);
  const mkTexRef = React.useRef(null);
  const baseRef = React.useRef(null);
  const desiredBaseColorRef = React.useRef("black");
  const controlsRef = React.useRef(null);
  const autoRotateRef = React.useRef(autoRotate);
  React.useEffect(() => {
    autoRotateRef.current = autoRotate;
    if (controlsRef.current) controlsRef.current.autoRotate = autoRotate;
  }, [autoRotate]);
  const labelRef = React.useRef(label);
  React.useEffect(() => {
    labelRef.current = label;
  });
  React.useEffect(() => {
    if (threeReady) return;
    let cancelled = false;
    const onReady = () => {
      if (!cancelled) setThreeReady(true);
    };
    window.addEventListener("cf-three-ready", onReady);
    const id = setInterval(() => {
      if (window.__cfThree) {
        onReady();
        clearInterval(id);
      }
    }, 200);
    const giveUp = setTimeout(() => {
      if (!cancelled && !window.__cfThree) setPhase("error");
    }, 6e3);
    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(giveUp);
      window.removeEventListener("cf-three-ready", onReady);
    };
  }, []);
  React.useEffect(() => {
    function onChange(e) {
      const c = e && e.detail && e.detail.color;
      if (c !== "black" && c !== "white") return;
      desiredBaseColorRef.current = c;
      const m = baseRef.current;
      if (!m || !m.material) return;
      if (c === "white") {
        m.material.color.setHex(15790058);
        m.material.roughness = 0.78;
      } else {
        m.material.color.setHex(2896186);
        m.material.roughness = 0.82;
      }
      m.material.needsUpdate = true;
    }
    window.addEventListener("cf:base-color-change", onChange);
    return () => window.removeEventListener("cf:base-color-change", onChange);
  }, []);
  React.useEffect(() => {
    if (!url) setPhase("error");
  }, [url]);
  React.useEffect(() => {
    if (!threeReady || !url || !mountRef.current) return;
    const mount = mountRef.current;
    const { THREE, GLTFLoader, OrbitControls, STLLoader } = window.__cfThree;
    let renderer, scene, camera, controls, root, rig, base, holder, plate, raf, ro;
    let disposed = false;
    let waitRO = null, waitRaf = 0, started = false;
    const userInteractRef = { current: false };
    const start = () => {
      if (started || disposed) return;
      started = true;
      if (waitRO) {
        waitRO.disconnect();
        waitRO = null;
      }
      if (waitRaf) {
        cancelAnimationFrame(waitRaf);
        waitRaf = 0;
      }
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      scene = new THREE.Scene();
      scene.background = null;
      camera = new THREE.PerspectiveCamera(36, w / h, 0.1, 5e3);
      const HERO_POLAR = Math.PI * 0.26;
      const HERO_R = 320;
      camera.position.set(0, HERO_R * Math.cos(HERO_POLAR), HERO_R * Math.sin(HERO_POLAR));
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.setSize(w, h);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1;
      if ("outputColorSpace" in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
      mount.appendChild(renderer.domElement);
      const hemi = new THREE.HemisphereLight(16777215, 12107191, 0.7);
      hemi.position.set(0, 1, 0);
      scene.add(hemi);
      scene.add(new THREE.AmbientLight(16777215, 0.18));
      const key = new THREE.DirectionalLight(16770508, 0.85);
      key.position.set(2, 3, 4);
      scene.add(key);
      const rim = new THREE.DirectionalLight(13226716, 0.45);
      rim.position.set(-3, 1.5, -2);
      scene.add(rim);
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enablePan = false;
      controls.enableZoom = false;
      controls.autoRotate = autoRotateRef.current;
      controls.autoRotateSpeed = autoRotateSpeed;
      controlsRef.current = controls;
      controls.minPolarAngle = Math.PI * 0.08;
      controls.maxPolarAngle = Math.PI * 0.55;
      controls.addEventListener("start", () => {
        userInteractRef.current = true;
        controls.autoRotate = false;
      });
      controls.addEventListener("end", () => {
        setTimeout(() => {
          if (!disposed) controls.autoRotate = autoRotateRef.current;
        }, 3e3);
      });
      const ACC_RATIO = 104.6 / 89.5;
      const accUrl = (f) => window.cfPaths && window.cfPaths.accessories && window.cfPaths.accessories[f] || "/accessory/" + encodeURIComponent(f);
      let fit0 = null;
      let fitParams = null;
      const computeFitFromBase = (bg) => {
        bg.computeBoundingBox();
        let bb = bg.boundingBox;
        const bFootX = bb.max.x - bb.min.x || 1;
        const s = fit0.modelFoot * ACC_RATIO / bFootX;
        bg.scale(s, s, s);
        bg.computeBoundingBox();
        bb = bg.boundingBox;
        const cx = (bb.min.x + bb.max.x) / 2;
        const cz = (bb.min.z + bb.max.z) / 2;
        const thick = bb.max.y - bb.min.y;
        fitParams = {
          s,
          tx: -cx,
          tz: -cz,
          ty: fit0.modelMinY - bb.max.y + thick * 0.02
          // seat base top at city underside
        };
        bg.translate(fitParams.tx, fitParams.ty, fitParams.tz);
        return bg;
      };
      const applyFit = (g) => {
        g.scale(fitParams.s, fitParams.s, fitParams.s);
        g.translate(fitParams.tx, fitParams.ty, fitParams.tz);
        return g;
      };
      const makeLabelTexture = (lab) => {
        const S = 24;
        const cv = document.createElement("canvas");
        cv.width = Math.round(57.6 * S);
        cv.height = Math.round(7.6 * S);
        const x = cv.getContext("2d");
        const mmX = (v) => v * S, mmY = (v) => v * S;
        x.fillStyle = "#2C313A";
        x.fillRect(0, 0, cv.width, cv.height);
        const pearl = "#C8CCD0";
        const hasCoord = lab.showCoords !== false && !!lab.coord;
        if (hasCoord) {
          x.strokeStyle = pearl;
          x.lineWidth = mmX(0.25);
          x.beginPath();
          x.moveTo(mmX(32.6), mmY(1.9));
          x.lineTo(mmX(32.6), mmY(5.7));
          x.stroke();
        }
        x.fillStyle = pearl;
        x.textBaseline = "alphabetic";
        const FF = "'Inter','Helvetica Neue',Arial,sans-serif";
        const set = (w2, sz, ls) => {
          x.font = `${w2} ${mmY(sz)}px ${FF}`;
          x.letterSpacing = `${mmX(ls)}px`;
        };
        const tx = hasCoord ? 3 : 28.8;
        const ta = hasCoord ? "left" : "center";
        x.textAlign = ta;
        if (lab.subline) {
          set(600, 1.55, 0.45);
          x.fillText(lab.title, mmX(tx), mmY(2.91));
          set(600, 2, 0.18);
          x.fillText(lab.subline, mmX(tx), mmY(5.81));
        } else {
          set(600, 2.4, 0.28);
          x.fillText(lab.title, mmX(tx), mmY(4.7));
        }
        if (hasCoord) {
          x.textAlign = "right";
          set(400, 1.8, 0.16);
          x.fillText(lab.coord, mmX(54.6), mmY(4.46));
        }
        const tex = new THREE.CanvasTexture(cv);
        if ("SRGBColorSpace" in THREE) tex.colorSpace = THREE.SRGBColorSpace;
        else if (THREE.sRGBEncoding) tex.encoding = THREE.sRGBEncoding;
        tex.needsUpdate = true;
        return tex;
      };
      const buildTagMesh = () => {
        const L = 57.6, H = 7.6, TH = 0.8, R = 3.8;
        const sh = new THREE.Shape();
        sh.moveTo(-L / 2 + R, -H / 2);
        sh.lineTo(L / 2 - R, -H / 2);
        sh.absarc(L / 2 - R, 0, R, -Math.PI / 2, Math.PI / 2, false);
        sh.lineTo(-L / 2 + R, H / 2);
        sh.absarc(-L / 2 + R, 0, R, Math.PI / 2, Math.PI * 1.5, false);
        const uvGen = {
          generateTopUV(g, vs, a, b, cc) {
            const f = (i) => new THREE.Vector2((vs[i * 3] + L / 2) / L, (vs[i * 3 + 1] + H / 2) / H);
            return [f(a), f(b), f(cc)];
          },
          generateSideWallUV() {
            const z = new THREE.Vector2(0, 0);
            return [z, z, z, z];
          }
        };
        const tg = new THREE.ExtrudeGeometry(sh, { depth: TH, bevelEnabled: false, UVGenerator: uvGen });
        tg.rotateY(-Math.PI / 2);
        tg.rotateZ(-0.24495);
        tg.computeBoundingBox();
        const c = tg.boundingBox.getCenter(new THREE.Vector3());
        const TX = -4.91, TY = -3.652, TZ = -44.684;
        tg.translate(TX - c.x, TY - c.y, TZ - c.z);
        applyFit(tg);
        const liveLabel = labelRef.current || label;
        const mat = liveLabel ? new THREE.MeshStandardMaterial({ map: makeLabelTexture(liveLabel), color: 16777215, metalness: 0, roughness: 0.5 }) : new THREE.MeshStandardMaterial({ color: 13225425, metalness: 0.85, roughness: 0.35 });
        const m = new THREE.Mesh(tg, mat);
        m.name = "plate";
        return m;
      };
      mkTexRef.current = makeLabelTexture;
      loadGlb(url, THREE, GLTFLoader).then((gltf) => {
        if (disposed) return;
        root = gltf.scene;
        root.rotation.x = -Math.PI / 2;
        let bb = new THREE.Box3().setFromObject(root);
        const sz = bb.getSize(new THREE.Vector3());
        const fitDim = Math.max(Math.hypot(sz.x, sz.z), sz.y) || 1;
        const visV = 2 * HERO_R * Math.tan(THREE.MathUtils.degToRad(36 / 2));
        const visMin = Math.min(visV, visV * (w / h));
        const targetWorld = visMin * 0.82;
        root.scale.setScalar(targetWorld / fitDim);
        bb = new THREE.Box3().setFromObject(root);
        root.position.sub(bb.getCenter(new THREE.Vector3()));
        root.traverse((o) => {
          if (o.isMesh && o.material) {
            const m = o.material;
            if ("metalness" in m) m.metalness = 0;
            if ("roughness" in m) m.roughness = 0.95;
            if ("flatShading" in m) m.flatShading = true;
            m.needsUpdate = true;
          }
        });
        rig = new THREE.Group();
        rig.add(root);
        const mb = new THREE.Box3().setFromObject(root);
        const ms = mb.getSize(new THREE.Vector3());
        fit0 = { modelFoot: Math.max(ms.x, ms.z) || 1, modelMinY: mb.min.y };
        rig.rotation.x = 0;
        rig.rotation.y = 0;
        scene.add(rig);
        controls.target.set(0, 0, 0);
        controls.update();
        setPhase("ready");
        if (!STLLoader) return;
        if (noAccessories) return;
        const loader = new STLLoader();
        const recentreRig = () => {
          if (disposed || !rig) return;
          rig.updateMatrixWorld(true);
          const c = new THREE.Box3().setFromObject(rig).getCenter(new THREE.Vector3());
          rig.position.y -= c.y;
          rig.updateMatrixWorld(true);
        };
        let resolveBaseReady;
        const baseReady = new Promise((res) => {
          resolveBaseReady = res;
        });
        loader.load(accUrl("9x9 base (base only).stl"), (g) => {
          if (disposed) return;
          g.computeVertexNormals();
          computeFitFromBase(g);
          base = new THREE.Mesh(g, new THREE.MeshStandardMaterial({
            color: 2896186,
            metalness: 0,
            roughness: 0.82,
            flatShading: true
          }));
          base.name = "base";
          rig.add(base);
          baseRef.current = base;
          if (desiredBaseColorRef.current === "white") {
            base.material.color.setHex(15790058);
            base.material.roughness = 0.78;
            base.material.needsUpdate = true;
          }
          recentreRig();
          resolveBaseReady();
        }, void 0, () => {
        });
        loader.load(accUrl("9x9 base (location tag holder).stl"), (hg) => {
          if (disposed) return;
          hg.computeVertexNormals();
          baseReady.then(() => {
            if (disposed || !base) {
              if (hg.dispose) hg.dispose();
              return;
            }
            applyFit(hg);
            holder = new THREE.Mesh(hg, new THREE.MeshStandardMaterial({
              color: 16119539,
              metalness: 0,
              roughness: 0.7,
              flatShading: true
            }));
            holder.name = "tagholder";
            rig.add(holder);
            rig.updateMatrixWorld(true);
            const baseC = new THREE.Box3().setFromObject(base).getCenter(new THREE.Vector3());
            const holdC = new THREE.Box3().setFromObject(holder).getCenter(new THREE.Vector3());
            const fx = holdC.x - baseC.x, fz = holdC.z - baseC.z;
            if (fx * fx + fz * fz > 1e-6) {
              const azim = Math.atan2(fx, fz);
              rig.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), -azim);
              rig.updateMatrixWorld(true);
            }
            plate = buildTagMesh();
            rig.add(plate);
            plateRef.current = plate;
            recentreRig();
          });
        }, void 0, () => {
        });
      }).catch((err) => {
        console.warn("[Model3DViewer] GLB load failed:", err);
        if (!disposed) setPhase("error");
      });
      const animate = () => {
        raf = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();
      ro = new ResizeObserver(() => {
        if (disposed) return;
        const nw = mount.clientWidth, nh = mount.clientHeight;
        if (!nw || !nh) return;
        camera.aspect = nw / nh;
        camera.updateProjectionMatrix();
        renderer.setSize(nw, nh);
      });
      ro.observe(mount);
    };
    if (mount.clientWidth > 0 && mount.clientHeight > 0) {
      start();
    } else {
      if (typeof ResizeObserver !== "undefined") {
        waitRO = new ResizeObserver(() => {
          if (!disposed && mount.clientWidth > 0 && mount.clientHeight > 0) start();
        });
        waitRO.observe(mount);
      }
      const poll = () => {
        if (disposed || started) return;
        if (mount.clientWidth > 0 && mount.clientHeight > 0) start();
        else waitRaf = requestAnimationFrame(poll);
      };
      waitRaf = requestAnimationFrame(poll);
    }
    return () => {
      disposed = true;
      if (waitRO) waitRO.disconnect();
      if (waitRaf) cancelAnimationFrame(waitRaf);
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      if (controls) controls.dispose();
      const disposeTree = (obj) => obj && obj.traverse((o) => {
        if (o.isMesh) {
          if (o.geometry) o.geometry.dispose();
          if (o.material) {
            const mats = Array.isArray(o.material) ? o.material : [o.material];
            mats.forEach((m) => {
              if (m.map) m.map.dispose();
              m.dispose();
            });
          }
        }
      });
      disposeTree(root);
      disposeTree(base);
      disposeTree(holder);
      disposeTree(plate);
      if (renderer) {
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      plateRef.current = null;
      mkTexRef.current = null;
      baseRef.current = null;
      controlsRef.current = null;
    };
  }, [threeReady, url, autoRotateSpeed]);
  const _labTitle = label && label.title;
  const _labSubline = label && label.subline;
  const _labCoord = label && label.coord;
  const _labShowCoords = label && label.showCoords;
  React.useEffect(() => {
    const plate = plateRef.current;
    const mkTex = mkTexRef.current;
    if (!plate || !mkTex || !label) return;
    const oldMap = plate.material && plate.material.map;
    const newMap = mkTex(label);
    plate.material.map = newMap;
    plate.material.needsUpdate = true;
    if (oldMap && typeof oldMap.dispose === "function") oldMap.dispose();
  }, [_labTitle, _labSubline, _labCoord, _labShowCoords]);
  return /* @__PURE__ */ React.createElement("div", { className, style: {
    position: "relative",
    width: "100%",
    height,
    background: "transparent",
    ...style
  } }, /* @__PURE__ */ React.createElement("div", { ref: mountRef, style: { width: "100%", height: "100%" } }), phase !== "ready" && /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: phase === "error" ? "none" : "auto"
  } }, phase === "error" && fallback ? /* @__PURE__ */ React.createElement("img", { src: fallback, alt: "", style: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    filter: "contrast(1.04)"
  } }) : /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { fontSize: 9, color: "var(--stone)" } }, phase === "loading" ? "LOADING MODEL\u2026" : "PREVIEW UNAVAILABLE")), showHint && phase === "ready" && /* @__PURE__ */ React.createElement("div", { style: {
    position: "absolute",
    bottom: 14,
    left: "50%",
    transform: "translateX(-50%)",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "5px 12px",
    background: "rgba(247,248,246,0.85)",
    border: "1px solid var(--ink)"
  } }, /* @__PURE__ */ React.createElement("span", { style: { width: 6, height: 6, background: "var(--ink)", borderRadius: "50%" } }), /* @__PURE__ */ React.createElement("span", { className: "eyebrow", style: { fontSize: 9 } }, "DRAG TO ROTATE")));
}
Object.assign(window, { Model3DViewer });
window.Model3DViewer = Model3DViewer;
})();
